/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useCallback, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {forwardRef, Box, Flex, Button, Stack, Text, Divider, Portal, HStack} from '@chakra-ui/react'
import {HYBRID_CART} from '../../constants'
import HybridLink from '../hybrid-link'
import {usePromoCode} from '../promo-code'
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import {EditIcon} from '../icons'
import OrderSummaryHeading from './order-summary-heading'
import OrderSummaryList from './order-summary-list'
import OrderSummaryListItem from './order-summary-list-item'
import OrderSummaryPriceAdjustmentItem from './order-summary-price-adjustment-item'
import OrderSummaryPromoForm from './order-summary-promo-form'
import OrderSummaryProvider from './order-summary-provider'
import OrderSummaryShipping from './order-summary-shipping'
import OrderSummarySubtotal from './order-summary-subtotal'
import OrderSummaryTax from './order-summary-tax'
import OrderSummaryTotal from './order-summary-total'

export const CartItems = forwardRef(({basket, colorScheme, expanded, onToggle}, ref) => {
    const [overflowHeight, setOverflowHeight] = useState(0)
    let secondProduct = useRef()

    useEffect(() => {
        if (!expanded) return
        let updatedOverflowHeight = secondProduct?.current?.offsetTop
        setOverflowHeight(updatedOverflowHeight)
    }, [expanded])

    return (
        <Stack ref={ref} spacing={4} width="full">
            <Flex justify="space-between">
                <Text textStyle="Regular Medium">
                    <FormattedMessage
                        description="# item(s) in cart"
                        defaultMessage="{itemCount, plural, =0 {0 Products} one {# Product} other {# Products}}"
                        id="order_summary.cart_items.action.num_of_items_in_cart"
                        values={{
                            itemCount:
                                basket.itemAccumulatedCount ||
                                (basket.productItems
                                    ? basket.productItems.reduce((a, b) => a + b.quantity, 0)
                                    : 0)
                        }}
                    />
                </Text>
                <Button
                    variant="link"
                    onClick={onToggle}
                    aria-controls="cart-items"
                    aria-expanded={expanded}
                >
                    {expanded ? (
                        <FormattedMessage defaultMessage="Hide" id="global.hide" />
                    ) : (
                        <FormattedMessage defaultMessage="Show" id="global.show" />
                    )}
                </Button>
            </Flex>

            <Divider colorScheme={colorScheme} />

            {expanded && (
                <Box id="cart-items">
                    <Box
                        overflowY={basket.productItems?.length > 2 ? 'scroll' : 'none'}
                        pr="1rem"
                        maxHeight={overflowHeight}
                    >
                        <Stack as="ul" spacing="4" py="2" align="flex-start" position="relative">
                            {basket.productItems?.map((product, idx) => {
                                const variant = {
                                    ...product,
                                    ...(basket._productItemsDetail &&
                                        basket._productItemsDetail[product.productId]),
                                    price: product.price
                                }
                                return (
                                    <ItemVariantProvider
                                        key={product.itemId}
                                        index={idx}
                                        variant={variant}
                                    >
                                        <HStack
                                            as="li"
                                            className={`order-item-${idx}`}
                                            width="full"
                                            spacing={2}
                                            align="flex-start"
                                            data-testid="order-summary-cart-item"
                                            ref={idx === 2 ? secondProduct : null}
                                        >
                                            <CartItemVariantImage
                                                width="20"
                                                link={variant['c_product-type'] !== 'Engraving'}
                                            />
                                            <Stack spacing={2} w="full">
                                                <CartItemVariantName
                                                    link={variant['c_product-type'] !== 'Engraving'}
                                                />
                                                <CartItemVariantAttributes includeQuantity />
                                                <CartItemVariantPrice
                                                    variant="order"
                                                    productLineItem={product}
                                                />
                                            </Stack>
                                        </HStack>
                                    </ItemVariantProvider>
                                )
                            })}
                        </Stack>
                    </Box>

                    {basket._type !== 'order' && (
                        <Stack spacing={4} width="full" align="flex-start">
                            <Button
                                as={HybridLink}
                                page={HYBRID_CART}
                                href="/appcart"
                                variant="link"
                                leftIcon={<EditIcon boxSize={4} />}
                            >
                                <FormattedMessage
                                    defaultMessage="Edit in cart"
                                    id="order_summary.cart_items.link.edit_cart"
                                />
                            </Button>

                            <Divider colorScheme={colorScheme} />
                        </Stack>
                    )}
                </Box>
            )}
        </Stack>
    )
})

CartItems.displayName = 'CartItems'

CartItems.propTypes = {
    basket: PropTypes.object,
    colorScheme: PropTypes.string,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func
}

/**
 * Original pwa component shared between checkout and cart,
 * but it is split into individual components as both pages
 * need different rendering.
 * @deprecated
 */
const OrderSummary = ({
    basket,
    colorScheme,
    heading,
    showPromoCodeForm = false,
    showCartItems = false,
    isEstimate = false,
    briefContainer,
    ...props
}) => {
    const [cartItemsExpanded, setCartItemsExpanded] = useState(false)
    const [, setRerenderOrderSummary] = useState(1)
    const cartItemsRef = useRef(null)

    const {removePromoCode} = usePromoCode()

    useEffect(() => {
        setRerenderOrderSummary((cnt) => cnt + 1)
    }, [basket?.itemCount, basket?.orderTotal])

    const expandAndFocusCartItems = useCallback(() => {
        setCartItemsExpanded(true)
        if (cartItemsRef.current) {
            if (cartItemsRef.current.scrollIntoView) {
                cartItemsRef.current.scrollIntoView({behavior: 'smooth'})
            } else {
                // fallback to focusing if scrollIntoView is not supported
                const tabindex = cartItemsRef.current.getAttribute('tabindex')
                cartItemsRef.current.setAttribute('tabindex', '-1')
                cartItemsRef.current.focus()
                cartItemsRef.current.setAttribute('tabindex', tabindex)
            }
        }
    }, [])

    if (!basket.basketId && !basket.orderNo) {
        return null
    }

    return (
        <OrderSummaryProvider basket={basket}>
            <Stack data-testid="sf-order-summary" spacing={4} {...props}>
                <OrderSummaryHeading>{heading}</OrderSummaryHeading>

                <Stack spacing={4} align="flex-start">
                    {showCartItems && (
                        <CartItems
                            ref={cartItemsRef}
                            basket={basket}
                            colorScheme={colorScheme}
                            expanded={cartItemsExpanded}
                            onToggle={() => setCartItemsExpanded(!cartItemsExpanded)}
                        />
                    )}

                    <OrderSummaryList>
                        <OrderSummaryListItem>
                            <OrderSummarySubtotal />
                        </OrderSummaryListItem>

                        {basket.orderPriceAdjustments?.map((adjustment) => (
                            <OrderSummaryListItem key={adjustment.priceAdjustmentId}>
                                <OrderSummaryPriceAdjustmentItem adjustment={adjustment} />
                            </OrderSummaryListItem>
                        ))}

                        <OrderSummaryListItem>
                            <OrderSummaryShipping />
                        </OrderSummaryListItem>

                        <OrderSummaryListItem>
                            <OrderSummaryTax />
                        </OrderSummaryListItem>
                    </OrderSummaryList>

                    {showPromoCodeForm ? (
                        <Box w="full">
                            <OrderSummaryPromoForm />
                        </Box>
                    ) : (
                        <Divider colorScheme={colorScheme} />
                    )}

                    <Stack spacing={4} w="full" textStyle="Semi Bold Medium">
                        <Flex w="full" justify="space-between">
                            <OrderSummaryTotal isEstimate={isEstimate} />
                        </Flex>

                        {basket.couponItems?.length > 0 && (
                            <Stack
                                p={4}
                                border="1px solid"
                                borderzColor="gray.100"
                                borderRadius="base"
                                bg="white"
                                data-testid="sf-order-summary-coupon-items"
                            >
                                <Text fontWeight="medium">
                                    <FormattedMessage
                                        defaultMessage="Promotions applied"
                                        id="order_summary.label.promotions_applied"
                                    />
                                    :
                                </Text>
                                <Stack as="ul">
                                    {basket.couponItems.map((item) => (
                                        <Flex as="li" key={item.couponItemId} alignItems="center">
                                            <Text
                                                flex="1"
                                                fontSize="sm"
                                                textTransform="uppercase"
                                                color="gray.800"
                                            >
                                                {item.code}
                                            </Text>
                                            {!basket.orderNo && (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    colorScheme="red"
                                                    onClick={() =>
                                                        removePromoCode(item.couponItemId)
                                                    }
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Remove"
                                                        id="global.remove"
                                                    />
                                                </Button>
                                            )}
                                        </Flex>
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>

                {briefContainer ? (
                    <Portal containerRef={briefContainer}>
                        <Flex justify="space-between">
                            <Button variant="link" onClick={expandAndFocusCartItems}>
                                <FormattedMessage
                                    description="# item(s) in cart"
                                    defaultMessage="{itemCount, plural, =0 {0 Products} one {# Product} other {# Products}}"
                                    id="order_summary.cart_items.action.num_of_items_in_cart"
                                    values={{itemCount: basket.itemAccumulatedCount}}
                                />
                            </Button>

                            <Text textStyle="Regular Medium">
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={basket?.productSubTotal}
                                />
                            </Text>
                        </Flex>
                    </Portal>
                ) : null}
            </Stack>
        </OrderSummaryProvider>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    briefContainer: PropTypes.object,
    colorScheme: PropTypes.string,
    heading: PropTypes.string,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool
}

export default OrderSummary
